import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Home } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { getPostBySlug, blogPosts } from '../../data/blogPosts';
import SEOHead from '../SEOHead';
import BlogCard from './BlogCard';

/**
 * 博客文章详情页
 */
function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const post = getPostBySlug(slug);

  // 文章不存在
  if (!post) {
    return (
      <div className="bg-[#faf9f6] dark:bg-gray-900 flex items-center justify-center min-h-[80vh]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            404
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {language === 'zh' ? '文章不存在' : 'Article not found'}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回博客' : 'Back to Blog'}
          </Link>
        </div>
      </div>
    );
  }

  const title = post.title[language];
  const content = post.content[language];
  const category = post.category[language];

  // 获取相关文章（同分类的其他文章）
  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category[language] === category)
    .slice(0, 2);

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* SEO Head */}
      <SEOHead article={post} />

      {/* 面包屑导航 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1">
              <Home size={14} />
              {language === 'zh' ? '首页' : 'Home'}
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-indigo-600 dark:hover:text-indigo-400">
              {language === 'zh' ? '博客' : 'Blog'}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{title}</span>
          </nav>
        </div>
      </div>

      {/* 文章内容 */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* 分类标签 */}
        <div className="mb-4">
          <span className="px-3 py-1 text-sm font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
            {category}
          </span>
        </div>

        {/* 标题 */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
          {title}
        </h1>

        {/* 元信息 */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="font-medium">{post.author}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar size={16} />
            <span>{post.date}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={16} />
            <span>{post.readTime} {language === 'zh' ? '分钟阅读' : 'min read'}</span>
          </div>
        </div>

        {/* 文章正文 */}
        <div
          className="prose prose-lg dark:prose-invert max-w-none
            prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
            prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
            prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
            prose-code:text-indigo-600 dark:prose-code:text-indigo-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
            prose-pre:bg-gray-900 prose-pre:text-gray-100
            prose-ul:my-4 prose-li:my-1
            prose-table:border-collapse prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2 prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-2
            prose-lead:text-xl prose-lead:text-gray-600 dark:prose-lead:text-gray-400 prose-lead:leading-relaxed
          "
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* 返回按钮 */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回博客' : 'Back to Blog'}
          </Link>
        </div>

        {/* 相关文章 */}
        {relatedPosts.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {language === 'zh' ? '相关文章' : 'Related Articles'}
            </h2>
            <div className="space-y-6">
              {relatedPosts.map(relatedPost => (
                <BlogCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}

export default BlogPost;
